/**
 * Ordered list extension that preserves a `class` attribute on <ol> (e.g. for AI diff highlights).
 * Uses a custom NodeView so the class is applied to the rendered element.
 */
import OrderedList from "@tiptap/extension-ordered-list";
import { mergeAttributes } from "@tiptap/core";

export const OrderedListWithClass = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class") || null,
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: "ol",
        getAttrs: (dom) => ({
          class: dom.getAttribute?.("class") || null,
          start: dom.hasAttribute("start")
            ? parseInt(dom.getAttribute("start") || "", 10)
            : 1,
        }),
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const { start, ...rest } = HTMLAttributes;
    const classValue = node.attrs.class;
    const merged = mergeAttributes(
      this.options.HTMLAttributes,
      rest,
      classValue ? { class: classValue } : {}
    );
    return start === 1 || start === undefined
      ? ["ol", merged, 0]
      : ["ol", mergeAttributes(merged, { start }), 0];
  },
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const ol = document.createElement("ol");
      const start = node.attrs.start;
      if (start !== undefined && start !== 1)
        ol.setAttribute("start", String(start));
      const cls = node.attrs.class;
      if (cls) ol.className = cls;
      mergeAttributes(ol, this.options.HTMLAttributes, HTMLAttributes);
      return {
        dom: ol,
        contentDOM: ol,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          const newStart = updatedNode.attrs.start;
          if (newStart !== undefined && newStart !== 1)
            ol.setAttribute("start", String(newStart));
          else ol.removeAttribute("start");
          const newCls = updatedNode.attrs.class;
          ol.className = newCls || "";
          return true;
        },
      };
    };
  },
});
